import React, { useMemo, useState } from 'react';
import { FiChevronRight, FiHome, FiUser } from 'react-icons/fi';
import { EmptyState, GapNotice } from './states';
import { PMS_LABELS } from './derive';

const TABS = [
  { id: 'hosts', label: 'New hosts' },
  { id: 'properties', label: 'New properties' },
];

const formatRelative = (date) => {
  if (!date) return '—';
  const ms = Date.now() - new Date(date).getTime();
  if (!Number.isFinite(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function RecentChangesPanel({
  hosts = [],
  properties = [],
  hostsLoading = false,
  propertiesAvailable = false,
  onSelectHost,
  onSelectProperty,
}) {
  const [tab, setTab] = useState('hosts');

  const sortedHosts = useMemo(
    () =>
      [...hosts]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 50),
    [hosts]
  );

  const sortedProps = useMemo(
    () =>
      [...properties]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 50),
    [properties]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-zinc-100 px-3 pt-2">
        <h3 className="text-[13px] font-semibold text-zinc-900">Recent changes</h3>
        {!propertiesAvailable && tab === 'properties' ? (
          <GapNotice label="Awaiting backend §B" />
        ) : null}
      </div>
      <div className="flex border-b border-zinc-100 px-3">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-2 py-2 text-[12px] font-medium transition ${
              tab === t.id
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t.label}
            <span className="ml-1.5 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600">
              {(t.id === 'hosts' ? sortedHosts.length : sortedProps.length).toLocaleString('en-US')}
            </span>
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === 'hosts' ? (
          hostsLoading && sortedHosts.length === 0 ? (
            <div className="space-y-2 p-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-zinc-100" />
              ))}
            </div>
          ) : sortedHosts.length === 0 ? (
            <EmptyState title="No new hosts in range" description="Try widening the date range." icon={FiUser} />
          ) : (
            <ul className="divide-y divide-zinc-100">
              {sortedHosts.map((h) => (
                <li key={h._id}>
                  <button
                    type="button"
                    onClick={() => onSelectHost?.(h)}
                    className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                      {(h.name || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-[12px] font-semibold text-zinc-900">{h.name}</p>
                        {h.pmsType && h.pmsType !== 'unknown' ? (
                          <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-indigo-700">
                            {PMS_LABELS[h.pmsType] || h.pmsType}
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate text-[11px] text-zinc-500">
                        {h.email || h.phoneNumber || '—'} · {h.propertyCount || 0} props
                      </p>
                    </div>
                    <span className="text-[10px] text-zinc-400 group-hover:text-zinc-600">
                      {formatRelative(h.createdAt)}
                    </span>
                    <FiChevronRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-500" />
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : sortedProps.length === 0 ? (
          <EmptyState
            title={propertiesAvailable ? 'No new properties in range' : 'Property timestamps unavailable'}
            description={
              propertiesAvailable
                ? 'Try widening the date range.'
                : 'Land backend proposal §B (createdAt on /properties) to see this stream.'
            }
            icon={FiHome}
          />
        ) : (
          <ul className="divide-y divide-zinc-100">
            {sortedProps.map((p) => (
              <li key={p._id}>
                <button
                  type="button"
                  onClick={() => onSelectProperty?.(p)}
                  className="group flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                    <FiHome className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-zinc-900">
                      {p?.address?.line1 || p?.listingTitle || p?.uniqueId || 'Property'}
                    </p>
                    <p className="truncate text-[11px] text-zinc-500">
                      {p?.address?.city || ''}{p?.address?.state ? `, ${p.address.state}` : ''}
                      {p.propertySource ? ` · ${PMS_LABELS[p.propertySource] || p.propertySource}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] text-zinc-400 group-hover:text-zinc-600">
                    {formatRelative(p.createdAt)}
                  </span>
                  <FiChevronRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-500" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
